import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Clock, CheckCircle2, XCircle, FileText, ExternalLink, UserCheck } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * V25 Phase 2 — Admin queue for tutor sensitive-field changes (name, gender,
 * qualification, id_proof, bank details). Each card shows old → new diff + a
 * "View document" link for id_proof changes.
 */
export default function TutorPendingChangesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deciding, setDeciding] = useState(null);

    const fetchPending = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/tutor-portal/pending-changes?limit=100");
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { fetchPending(); }, [fetchPending]);

    const decide = async (id, decision) => {
        const note = decision === "reject" ? window.prompt("Reason for rejection (optional):") || "" : "";
        setDeciding(id);
        try {
            await api.post(`/admin/tutor-portal/pending-changes/${id}/decide`,
                { decision, admin_note: note || null });
            toast.success(`Change ${decision === "approve" ? "approved" : "rejected"}`);
            setItems((prev) => prev.filter((x) => x.id !== id));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setDeciding(null); }
    };

    const viewDoc = async (docId) => {
        try {
            const res = await api.get(`/admin/tutor-portal/documents/${docId}/raw`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="tutor-pending-changes-panel">
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-lg text-[#0F172A] inline-flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-600" /> Pending Tutor Profile Changes
                    {items.length > 0 && (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded">
                            {items.length} awaiting
                        </span>
                    )}
                </h2>
                <button onClick={fetchPending} disabled={loading} className="text-xs text-[#1E3A8A] hover:underline" data-testid="tutor-pending-refresh">
                    {loading ? "Refreshing…" : "Refresh"}
                </button>
            </div>
            {loading ? (
                <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center border-2 border-dashed border-slate-200 rounded-lg" data-testid="tutor-pending-empty">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                    All caught up. No tutor profile changes awaiting your approval.
                </div>
            ) : (
                <div className="space-y-2" data-testid="tutor-pending-list">
                    {items.map((it) => {
                        const isDoc = it.field === "id_proof_document_id";
                        return (
                            <div key={it.id} data-testid={`tutor-pending-item-${it.id}`} data-field={it.field}
                                className="border border-slate-200 rounded-xl px-3 py-2.5 flex items-start justify-between gap-3 hover:bg-slate-50">
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-[#0F172A] inline-flex items-center gap-2 flex-wrap">
                                        {it.tutor?.name || it.tutor?.email || it.tutor_id}
                                        <span className="text-[10px] text-slate-500 font-normal">({it.tutor?.email})</span>
                                        <span className="text-[10px] uppercase tracking-wider bg-violet-100 text-violet-800 border border-violet-300 px-1.5 py-0.5 rounded">
                                            {it.field}
                                        </span>
                                    </div>
                                    <div className="text-xs text-slate-600 mt-1">
                                        {isDoc ? (
                                            <>
                                                <span className="text-slate-400">Uploaded new ID proof document.</span>{" "}
                                                <button onClick={() => viewDoc(it.new_value)} className="text-[#1E3A8A] hover:underline inline-flex items-center gap-0.5"
                                                    data-testid={`tutor-pending-view-doc-${it.id}`}>
                                                    <FileText className="w-3 h-3" /> View document <ExternalLink className="w-3 h-3" />
                                                </button>
                                            </>
                                        ) : (
                                            <span>
                                                <span className="text-red-600 line-through">{String(it.old_value || "—")}</span>{" "}
                                                <span className="text-slate-400">→</span>{" "}
                                                <span className="text-emerald-700 font-semibold">{String(it.new_value)}</span>
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-slate-400 mt-1">{new Date(it.created_at).toLocaleString()}</div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => decide(it.id, "approve")} disabled={deciding === it.id}
                                        data-testid={`tutor-pending-approve-${it.id}`}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60">
                                        {deciding === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                        Approve
                                    </button>
                                    <button onClick={() => decide(it.id, "reject")} disabled={deciding === it.id}
                                        data-testid={`tutor-pending-reject-${it.id}`}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-300 hover:bg-red-50 text-red-700 text-xs font-semibold disabled:opacity-60">
                                        <XCircle className="w-3 h-3" /> Reject
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
